<template>
  <div class="list-page-container">
    <!-- 顶部操作区 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-notebook-2"></i>
          <span>{{ $t('system.dictManagementTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleAddType" v-if="hasPermission('sys:dict:save')">
            <i class="el-icon-plus"></i> {{ $t('dictManagement.addDictType') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <el-form :inline="true" :model="searchForm" class="filter-form" @submit.native.prevent>
          <el-form-item>
            <el-input
              v-model="searchForm.dictName"
              :placeholder="$t('dictManagement.searchPlaceholder')"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </el-form-item>
          <div class="search-btns">
            <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
            <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
          </div>
        </el-form>
      </div>
    </div>

    <!-- 主体布局 -->
    <div class="dict-content-wrapper">
      <!-- 左侧：字典类型列表 -->
      <el-card class="dict-type-card" shadow="never">
        <div slot="header" class="card-header">
          <span>{{ $t('dictManagement.dictTypeName') }}</span>
        </div>
        <el-table
          :data="dictTypeList"
          v-loading="typeLoading"
          highlight-current-row
          @current-change="handleTypeChange"
          style="width: 100%"
          height="100%"
          class="custom-table"
        >
          <el-table-column prop="dictName" :label="$t('dictManagement.dictTypeName')" min-width="120" show-overflow-tooltip></el-table-column>
          <el-table-column prop="dictType" :label="$t('dictManagement.dictTypeCode')" min-width="150" show-overflow-tooltip></el-table-column>
          <el-table-column v-if="hasAnyTypeAction" :label="$t('common.operation')" :width="typeActionWidth" align="center" fixed="right">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button v-if="hasPermission('sys:dict:update')" type="warning" size="mini" plain icon="el-icon-edit" @click.stop="handleEditType(scope.row)">{{ $t('common.edit') }}</el-button>
                <el-button v-if="hasPermission('sys:dict:delete')" type="danger" size="mini" plain icon="el-icon-delete" @click.stop="handleDeleteType(scope.row)">{{ $t('common.delete') }}</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <div class="type-pagination pagination-container">
          <el-pagination
            small
            layout="prev, pager, next"
            :current-page="typePage"
            :page-size="typeLimit"
            :total="typeTotal"
            @current-change="handleTypePageChange"
          ></el-pagination>
        </div>
      </el-card>

      <!-- 右侧：字典数据列表 -->
      <el-card class="dict-data-card" shadow="never">
        <div slot="header" class="card-header">
          <span class="data-title">{{ currentDictType ? currentDictType.dictName + ' (' + currentDictType.dictType + ')' : $t('dictManagement.selectDictTypeFirst') }}</span>
          <el-button 
            type="primary" 
            size="mini" 
            icon="el-icon-plus" 
            @click="handleAddData" 
            v-if="currentDictType && hasPermission('sys:dict:save')"
          >
            {{ $t('dictManagement.addDictData') }}
          </el-button>
        </div>
        
        <el-table
          :data="dictDataList"
          v-loading="dataLoading"
          style="width: 100%"
          height="100%"
          class="custom-table"
        >
          <el-table-column prop="dictLabel" :label="$t('dictManagement.dictLabel')" min-width="120" show-overflow-tooltip></el-table-column>
          <el-table-column prop="dictValue" :label="$t('dictManagement.dictValue')" min-width="120" show-overflow-tooltip></el-table-column>
          <el-table-column prop="sort" :label="$t('dictManagement.sort')" width="80" align="center"></el-table-column>
          <el-table-column prop="remark" :label="$t('common.remark')" min-width="150" show-overflow-tooltip></el-table-column>
          <el-table-column v-if="hasAnyDataAction" :label="$t('common.operation')" :width="dataActionWidth" align="center" fixed="right">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button v-if="hasPermission('sys:dict:update')" type="warning" size="mini" plain icon="el-icon-edit" @click="handleEditData(scope.row)">{{ $t('common.edit') }}</el-button>
                <el-button v-if="hasPermission('sys:dict:delete')" type="danger" size="mini" plain icon="el-icon-delete" @click="handleDeleteData(scope.row)">{{ $t('common.delete') }}</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <div class="data-pagination pagination-container">
          <el-pagination
            background
            layout="total, prev, pager, next"
            :current-page="dataPage"
            :page-size="dataLimit"
            :total="dataTotal"
            @current-change="handleDataPageChange"
          ></el-pagination>
        </div>
      </el-card>
    </div>

    <!-- 字典类型弹窗 -->
    <el-dialog :title="typeDialogTitle" :visible.sync="typeDialogVisible" width="500px">
      <el-form :model="typeForm" :rules="typeDialogRules" ref="typeForm" label-width="100px">
        <el-form-item :label="$t('dictManagement.dictTypeName')" prop="dictName">
          <el-input v-model="typeForm.dictName"></el-input>
        </el-form-item>
        <el-form-item :label="$t('dictManagement.dictTypeCode')" prop="dictType">
          <el-input v-model="typeForm.dictType"></el-input>
        </el-form-item>
        <el-form-item :label="$t('dictManagement.sort')" prop="sort">
          <el-input-number v-model="typeForm.sort" :min="0"></el-input-number>
        </el-form-item>
        <el-form-item :label="$t('common.remark')" prop="remark">
          <el-input v-model="typeForm.remark" type="textarea" :rows="2"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="typeDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitTypeForm" :loading="submitLoading">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>

    <!-- 字典数据弹窗 -->
    <el-dialog :title="dataDialogTitle" :visible.sync="dataDialogVisible" width="500px">
      <el-form :model="dataForm" :rules="dataDialogRules" ref="dataForm" label-width="100px">
        <el-form-item :label="$t('dictManagement.dictLabel')" prop="dictLabel">
          <el-input v-model="dataForm.dictLabel"></el-input>
        </el-form-item>
        <el-form-item :label="$t('dictManagement.dictValue')" prop="dictValue">
          <el-input v-model="dataForm.dictValue"></el-input>
        </el-form-item>
        <el-form-item :label="$t('dictManagement.sort')" prop="sort">
          <el-input-number v-model="dataForm.sort" :min="0"></el-input-number>
        </el-form-item>
        <el-form-item :label="$t('common.remark')" prop="remark">
          <el-input v-model="dataForm.remark" type="textarea" :rows="2"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dataDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitDataForm" :loading="submitLoading">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'DictManagement',
  data() {
    return {
      typeLoading: false,
      dataLoading: false,
      submitLoading: false,
      dictTypeList: [],
      dictDataList: [],
      currentDictType: null,
      typeTotal: 0,
      typePage: 1,
      typeLimit: 10,
      dataTotal: 0,
      dataPage: 1,
      dataLimit: 10,
      searchForm: {
        dictName: ''
      },
      // Type Dialog
      typeDialogVisible: false,
      typeDialogTitle: '',
      typeForm: {
        id: null,
        dictName: '',
        dictType: '',
        sort: 0,
        remark: ''
      },
      // Data Dialog
      dataDialogVisible: false,
      dataDialogTitle: '',
      dataForm: {
        id: null,
        dictTypeId: null,
        dictLabel: '',
        dictValue: '',
        sort: 0,
        remark: ''
      }
    }
  },
  computed: {
    hasAnyTypeAction() {
      return this.hasPermission('sys:dict:update') || this.hasPermission('sys:dict:delete')
    },
    typeActionWidth() {
      let count = 0
      if (this.hasPermission('sys:dict:update')) count += 1
      if (this.hasPermission('sys:dict:delete')) count += 1
      const widthMap = { 1: 100, 2: 170 }
      return widthMap[count] || 0
    },
    hasAnyDataAction() {
      return this.hasPermission('sys:dict:update') || this.hasPermission('sys:dict:delete')
    },
    dataActionWidth() {
      let count = 0
      if (this.hasPermission('sys:dict:update')) count += 1
      if (this.hasPermission('sys:dict:delete')) count += 1
      const widthMap = { 1: 100, 2: 170 }
      return widthMap[count] || 0
    },
    typeDialogRules() {
      return {
        dictName: [{ required: true, message: this.$t('dictManagement.dictNameRequired'), trigger: 'blur' }],
        dictType: [{ required: true, message: this.$t('dictManagement.dictTypeRequired'), trigger: 'blur' }]
      }
    },
    dataDialogRules() {
      return {
        dictLabel: [{ required: true, message: this.$t('dictManagement.dictLabelRequired'), trigger: 'blur' }],
        dictValue: [{ required: true, message: this.$t('dictManagement.dictValueRequired'), trigger: 'blur' }]
      }
    }
  },
  created() {
    this.getTypeList()
  },
  methods: {
    hasPermission,
    async getTypeList() {
      this.typeLoading = true
      try {
        const res = await request({
          url: '/admin/dict/type/page',
          method: 'get',
          params: {
            page: this.typePage,
            limit: this.typeLimit,
            dictName: this.searchForm.dictName
          }
        })
        if (res) {
          this.dictTypeList = res.list || []
          this.typeTotal = res.total || 0
          if (this.dictTypeList.length > 0 && !this.currentDictType) {
            this.handleTypeChange(this.dictTypeList[0])
          }
        }
      } catch (error) {
        console.error('Fetch dict types error:', error)
      } finally {
        this.typeLoading = false
      }
    },
    async getDataList() {
      if (!this.currentDictType) return
      this.dataLoading = true
      try {
        const res = await request({
          url: '/admin/dict/data/page',
          method: 'get',
          params: {
            dictTypeId: this.currentDictType.id,
            page: this.dataPage,
            limit: this.dataLimit
          }
        })
        if (res) {
          this.dictDataList = res.list || []
          this.dataTotal = res.total || 0
        }
      } catch (error) {
        console.error('Fetch dict data error:', error)
      } finally {
        this.dataLoading = false
      }
    },
    handleSearch() {
      this.typePage = 1
      this.currentDictType = null
      this.getTypeList()
    },
    handleReset() {
      this.searchForm.dictName = ''
      this.handleSearch()
    },
    handleTypeChange(val) {
      if (!val) return
      this.currentDictType = val
      this.dataPage = 1
      this.getDataList()
    },
    handleTypePageChange(val) {
      this.typePage = val
      this.currentDictType = null
      this.getTypeList()
    },
    handleDataPageChange(val) {
      this.dataPage = val
      this.getDataList()
    },
    // Type operations
    handleAddType() {
      this.typeDialogTitle = this.$t('dictManagement.addDictType')
      this.typeForm = { id: null, dictName: '', dictType: '', sort: 0, remark: '' }
      this.typeDialogVisible = true
      this.$nextTick(() => { this.$refs.typeForm.clearValidate() })
    },
    handleEditType(row) {
      this.typeDialogTitle = this.$t('dictManagement.editDictType')
      this.typeForm = { ...row }
      this.typeDialogVisible = true
    },
    async submitTypeForm() {
      this.$refs.typeForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            const isEdit = !!this.typeForm.id
            const res = await request({
              url: `/admin/dict/type/${isEdit ? 'update' : 'save'}`,
              method: isEdit ? 'put' : 'post',
              data: this.typeForm
            })
            if (res !== undefined) {
              this.$message.success(this.$t('dictManagement.saveSuccess'))
              this.typeDialogVisible = false
              this.getTypeList()
            }
          } catch (error) {
            console.error('Submit dict type error:', error)
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    handleDeleteType(row) {
      this.$confirm(this.$t('dictManagement.confirmDeleteDictType'), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          const res = await request({
            url: '/admin/dict/type/delete',
            method: 'post',
            data: [row.id]
          })
          if (res !== undefined) {
            this.$message.success(this.$t('dictManagement.deleteSuccess'))
            if (this.currentDictType && this.currentDictType.id === row.id) {
              this.currentDictType = null
              this.dictDataList = []
            }
            this.getTypeList()
          }
        } catch (error) {
          console.error('Delete dict type error:', error)
        }
      })
    },
    // Data operations
    handleAddData() {
      if (!this.currentDictType) return
      this.dataDialogTitle = this.$t('dictManagement.addDictData')
      this.dataForm = { id: null, dictTypeId: this.currentDictType.id, dictLabel: '', dictValue: '', sort: 0, remark: '' }
      this.dataDialogVisible = true
      this.$nextTick(() => { 
        if (this.$refs.dataForm) {
          this.$refs.dataForm.clearValidate() 
        }
      })
    },
    handleEditData(row) {
      this.dataDialogTitle = this.$t('dictManagement.editDictData')
      this.dataForm = { ...row }
      this.dataDialogVisible = true
    },
    async submitDataForm() {
      this.$refs.dataForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            const isEdit = !!this.dataForm.id
            const res = await request({
              url: `/admin/dict/data/${isEdit ? 'update' : 'save'}`,
              method: isEdit ? 'put' : 'post',
              data: this.dataForm
            })
            if (res !== undefined) {
              this.$message.success(this.$t('dictManagement.saveSuccess'))
              this.dataDialogVisible = false
              this.getDataList()
            }
          } catch (error) {
            console.error('Submit dict data error:', error)
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    handleDeleteData(row) {
      this.$confirm(this.$t('dictManagement.confirmDeleteDictData'), this.$t('common.tips'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        try {
          const res = await request({
            url: '/admin/dict/data/delete',
            method: 'post',
            data: [row.id]
          })
          if (res !== undefined) {
            this.$message.success(this.$t('dictManagement.deleteSuccess'))
            this.getDataList()
          }
        } catch (error) {
          console.error('Delete dict data error:', error)
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 字典管理双面板布局 */
.dict-content-wrapper {
  flex: 1;
  display: flex;
  gap: $lp-spacing-lg;
  min-height: 0;
  overflow: hidden;

  .dict-type-card {
    flex: 0 0 500px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    @include table-scrollbar;

    :deep(.el-card__body) {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: $lp-spacing-md;
      min-height: 0;
      overflow: hidden;

      .custom-table {
        flex: 1;
        min-height: 0;
      }

      .type-pagination {
        margin-top: $lp-spacing-sm;
        flex-shrink: 0;
      }
    }
  }

  .dict-data-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    @include table-scrollbar;

    :deep(.el-card__body) {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: $lp-spacing-md;
      min-height: 0;
      overflow: hidden;

      .custom-table {
        flex: 1;
        min-height: 0;
      }

      .data-pagination {
        margin-top: $lp-spacing-sm;
        flex-shrink: 0;
      }
    }
  }
}

/* 响应式：小屏幕下上下堆叠 */
@media screen and (max-width: 1024px) {
  .dict-content-wrapper {
    flex-direction: column;

    .dict-type-card {
      flex: none;
      max-height: 45%;
    }

    .dict-data-card {
      flex: none;
      min-height: 50%;
    }
  }
}

/* 卡片头部与操作按钮 */
.dict-content-wrapper {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .data-title {
      font-size: $lp-font-size-md;
      color: $lp-text-primary;
      font-weight: 600;
    }
  }

  .delete-btn {
    color: #ef4444;
    &:hover { color: #dc2626; }
  }
}
</style>
